import { hash } from '@ember/helper';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBadge,
  PrismInput,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import type { HeaderTitle } from '@mylighthouse/prism-ember/components/prism/card';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import LoadingBar from 'lumen/components/general/loading-bar';
import outsideClickHandler from 'lumen/modifiers/outside-click-handler';
import { timeout } from 'lumen/utils/waiters';

interface ParityInsightBreakdownBlockTitleSignature {
  Args: {
    cardHeader: {
      title: HeaderTitle;
    };
    disabledTooltipMessage: string;
    isDisabled: boolean;
    params?: {
      blockDisplayName: string;
      isLoading: boolean;
      searchValues?: {
        isSearchActive: boolean;
        searchValue: string;
      };
      segmentationName: string;
      updateIsSearchActive: (value: boolean) => void;
      updateSearchValue: (value: string) => void;
    };
    redirectToTrends: (segmentationName: string | undefined) => void;
  };
}

export default class ParityInsightBreakdownBlockTitle extends Component<ParityInsightBreakdownBlockTitleSignature> {
  @tracked tempSearchValue = '';

  /**
   * This updates the search value in the controller
   * We keep using the temp search value at all times and assume nothing went wrong updating the value
   * If something did go wrong, we'll send the correct value down to the controller next time
   */
  updateSearchValueTask = task(
    { restartable: true },
    async (event): Promise<void> => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      this.tempSearchValue = event.target.value;
      await timeout();
      this.args.params?.updateSearchValue(this.tempSearchValue);
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get blockDisplayName() {
    return this.args.params?.blockDisplayName;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLoading() {
    return this.args.params?.isLoading;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSearchActive() {
    return this.searchValues?.isSearchActive;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get searchValue() {
    return this.searchValues?.searchValue;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get searchValues() {
    return this.args.params?.searchValues;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get segmentationName() {
    return this.args.params?.segmentationName;
  }

  @action disableSearch(): void {
    this.toggleSearch(false);
  }

  @action expandPopup(): void {
    this.args.redirectToTrends(this.segmentationName);
  }

  @action toggleSearch(isEnabled: boolean): void {
    this.args.params?.updateIsSearchActive(isEnabled);
  }

  <template>
    <div class="title-box flex items-center">
      {{#if this.isLoading}}
        <LoadingBar />
      {{else if this.isSearchActive}}
        <div class="flex flex-grow">
          <PrismInput
            @autofocus={{true}}
            @iconAfter={{hash name="search"}}
            @onInput={{this.updateSearchValueTask.perform}}
            @placeholder={{t "general.search_placeholder"}}
            @removePadding={{true}}
            @value={{this.tempSearchValue}}
            {{outsideClickHandler this.disableSearch}}
          />
        </div>
      {{else}}
        <div class="flex items-center gap-100">
          <@cardHeader.title class="truncate">
            {{this.blockDisplayName}}
          </@cardHeader.title>

          {{#if @isDisabled}}
            <span>
              <PrismBadge
                @iconName="circle-information-outline"
                @iconPosition="right"
                @label={{t "general.filtering_unavailable"}}
              />
              <PrismTooltip @text={{@disabledTooltipMessage}} />
            </span>
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
